import { Undo2 } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';

import { EnvironmentReconcileStatus } from '../types';

import { DriftDetails } from './DriftDetails';

type CorrectedResource = NonNullable<
  NonNullable<EnvironmentReconcileStatus['history']>[number]['resources']
>[number];

/**
 * Shows what one correction put back on one environment.
 *
 * A modal rather than an expanding row, for the reason drift uses one: a
 * resource changes in several places and a correction covers several
 * resources, so the patches push the rest of the table off the screen when
 * opened inline. The patches themselves are rendered the way drift renders
 * them, because they describe the same thing - what the cluster had that the
 * source did not.
 */
export function CorrectionModal({
  name,
  resources,
  onDismiss,
}: {
  name: string;
  resources: Array<CorrectedResource>;
  onDismiss: () => void;
}) {
  return (
    <Modal onDismiss={onDismiss} aria-label={`Corrected on ${name}`} size="lg">
      <Modal.Header title="What was put back" />
      <Modal.Body>
        <p className="text-muted !mb-3 text-xs">
          Each line is a path, then what the source declares → what the cluster
          had before it was corrected. Secrets are masked.
        </p>

        <div className="max-h-96 overflow-y-auto">
          <DriftDetails drift={resources} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={onDismiss}
          color="secondary"
          data-cy="close-correction"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** The count in the history table, which opens the patches when clicked. */
export function CorrectedCountButton({
  count,
  onClick,
  index,
}: {
  count: number;
  onClick: () => void;
  index: number;
}) {
  return (
    <Button
      color="link"
      icon={Undo2}
      onClick={onClick}
      className="!justify-start !p-0 text-left"
      data-cy={`workflow-correction-resources-${index}`}
    >
      {count} {pluralize(count, 'resource')}
    </Button>
  );
}
